import type { AnimationEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

const STYLE_ID = 'autofill-detection-styles';
const AUTOFILL_ANIMATION_NAME = 'detectAutoFill';

type UseAutofillDetectionResult = {
  hasAutoFill: boolean;
  onAnimationStart: (e: AnimationEvent<HTMLInputElement>) => void;
  reset: () => void;
};

/**
 * Hook for detecting browser autofill in input fields.
 *
 * Problem: Browsers (Chrome, Safari, Edge) do NOT change the `value` property
 * and do NOT trigger onChange/onInput events when autofilling forms.
 * This is a security measure, but it breaks UI logic
 * (e.g., placeholder/label doesn't float up when field is pre-filled).
 *
 * Solution: The hook injects a CSS animation that triggers when the browser
 * applies the `:-webkit-autofill` pseudo-class. This animation is intercepted
 * via the `onAnimationStart` event.
 *
 * @returns {boolean} hasAutoFill - Flag indicating the field was autofilled by browser
 * @returns {function} onAnimationStart - Animation event handler to attach to input element
 * @returns {function} reset - Function to reset autofill detection state
 *
 * @example
 * const InputComponent = () => {
 *   const { hasAutoFill, onAnimationStart } = useAutofillDetection();
 *   const shouldLabelFloat = isFocused || hasValue || hasAutoFill;
 *
 *   return (
 *     <input
 *       onAnimationStart={onAnimationStart}
 *       // ... other props
 *     />
 *   );
 * };
 */
export const useAutofillDetection = (): UseAutofillDetectionResult => {
  const [hasAutoFill, setHasAutoFill] = useState(false);

  // Injects CSS styles into the document once on first hook usage.
  // Styles create an animation attached to the `:-webkit-autofill` pseudo-class.
  // Browser will trigger this animation each time field gets autofilled.
  useEffect(() => {
    // Check if styles haven't been injected yet
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        @keyframes ${AUTOFILL_ANIMATION_NAME} {
          from { background-color: transparent; }
          to { background-color: transparent; }
        }
        input:-webkit-autofill {
          animation-name: ${AUTOFILL_ANIMATION_NAME};
          animation-duration: 0.001s;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Animation start event handler.
  // When browser applies autofill to a field, it triggers our animation.
  // We intercept this event and set the autofill flag.
  const handleAnimationStart = useCallback(
    (e: AnimationEvent<HTMLInputElement>) => {
      if (e.animationName === AUTOFILL_ANIMATION_NAME) {
        setHasAutoFill(true);
      }
    },
    []
  );

  // Resets the autofill detection state.
  // Useful when you need to programmatically clear the field after autofill.
  // Only resets state if field was previously autofilled.
  const reset = useCallback(
    () => hasAutoFill && setHasAutoFill(false),
    [hasAutoFill]
  );

  return {
    hasAutoFill,
    onAnimationStart: handleAnimationStart,
    reset,
  };
};
